// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QtyNumbers, { QtyNumbersStyles } from "@vanmeter/App/Components/Product/QtyNumbers";
import { sharedProductDescriptionStyles } from "@vanmeter/App/SharedStyles/ProductSharedStyles";
import Tooltip from "@vanmeter/MobiusOverrides/Tooltip/Tooltip";
import ProductAvailability, { ProductAvailabilityStyles } from "@vanmeter/Overrides/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@vanmeter/Overrides/Components/ProductPartNumbers";
import CartLineQuantity, { CartLineQuantityStyles } from "@vanmeter/Overrides/Widgets/Cart/CartLineQuantity";
import { fontConfig } from "@vanmeter/XDL/constants";
import Check from "@vanmeter/XDL/Icons/IconCheck";
import Close from "@vanmeter/XDL/Icons/IconClose";
import NotificationWarning from "@vanmeter/XDL/Icons/IconNotificationWarning";
import { color } from "@vanmeter/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    cart: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    cartLineImage?: ProductImageStyles;
    brand?: LinkPresentationProps;
    lineDescription?: LinkPresentationProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    qtyNumbersStyles?: QtyNumbersStyles;
    productAvailability?: ProductAvailabilityStyles;
    cartLineQuantityWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    basicProductPriceStyles?: ProductPriceStyles;
    removeCartLineLink?: LinkPresentationProps;
    removeConfirmation?: InjectableCss;
    removeConfirmationText?: TypographyProps;
    removeConfirmationButton?: ButtonPresentationProps;
    errorMessagesWrapper?: InjectableCss;
}

export const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            align-items: flex-start;
            display: grid;
            grid-template-columns: 96px 1fr 1fr 12px;
            gap: 8px 12px;
            padding-bottom: 12px;
        `,
    },
    cartLineImage: {
        linkWrappingImage: {
            css: css`
                grid-column: 1/2;
                grid-row: 1/5;
            `,
        },
        img: {
            width: "96px",
            height: "96px",
            css: css`
                aspect-ratio: 1;
            `,
        },
    },
    lineDescription: {
        ...sharedProductDescriptionStyles({ lines: 1, includeMinHeight: false }).productDetailLink,
        css: css`
            grid-column: 2/4;
        `,
    },
    partNumbersStyles: {
        container: {
            css: css`
                grid-column: 2/4;
            `,
        },
    },
    qtyNumbersStyles: {
        container: {
            css: css`
                grid-column: 2/4;
            `,
        },
    },
    productAvailability: {
        container: {
            css: css`
                grid-column: 2/4;
            `,
        },
        errorWrapper: {
            css: css`
                grid-column: 2/4;
            `,
        },
    },
    cartLineQuantityWrapper: {
        css: css`
            grid-column: 2/3;
            display: flex;
            gap: 8px;
            align-items: center;
        `,
    },
    basicProductPriceStyles: {
        wrapper: {
            css: css`
                grid-column: 3/4;
                align-self: flex-end;
                justify-self: flex-end;
                width: auto;
            `,
        },
        priceWrapper: {
            css: css`
                align-items: flex-end;
                gap: 4px;
            `,
        },
        price: {
            priceText: {
                ...fontConfig.regularSingleLine,
                color: "common.ui6",
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    ...fontConfig.smallSingleLine,
                },
            },
        },
        quoteMessage: {
            text: {
                ...fontConfig.regularSingleLine,
                color: "common.ui6",
            },
        },
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: Close,
                width: 12,
                height: 16,
                color: "common.ui6",
            } as any,
        },
        css: css`
            grid-column: 4/5;
            grid-row: 1;
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 8px;
            grid-column: 2/4;
            & > ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
    removeConfirmation: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-top: 1px solid ${color("common.border")};
            grid-column: 2 /5;
            padding-top: 8px;
            column-gap: 12px;
        `,
    },
    removeConfirmationText: {
        ...fontConfig.xSmall,
        weight: fontConfig.weight.bold,
        css: css`
            margin-right: auto;
        `,
    },
    removeConfirmationButton: {
        variant: "link" as any,
        css: css`
            font-size: ${fontConfig.xSmall.size}px;
            line-height: ${fontConfig.xSmallSingleLine.lineHeight};
            ${IconWrapper} {
                margin-right: 6px;
            }
        `,
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const showWarning = !!errorMessages?.length || cartLine.quoteRequired;

    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <ProductImage product={cartLine} extendedStyles={styles.cartLineImage} />
                <Link href={cartLine.productUri} aria-label="Cartline Description" {...styles.lineDescription}>
                    {cartLine.shortDescription}
                </Link>
                <ProductPartNumbers
                    customerProductNumber={cartLine.customerName}
                    manufacturerItem={cartLine.manufacturerItem}
                    productNumber={cartLine.erpNumber}
                    extendedStyles={styles.partNumbersStyles}
                    upcCode={cartLine.properties.upcCode}
                    showUpcCode={true}
                />
                <QtyNumbers
                    minimumOrderQty={Number(cartLine.properties.minimumOrderQty) || 0}
                    extendedStyles={styles.qtyNumbersStyles}
                />
                <ProductAvailability
                    productId={cartLine.productId!}
                    availability={cartLine.availability!}
                    unitOfMeasure={cartLine.unitOfMeasure}
                    trackInventory={cartLine.trackInventory}
                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                    extendedStyles={styles.productAvailability}
                    totalWarehouses={cartLine.qtyOnHand}
                    leadTime={cartLine.properties.leadTime}
                    additionalResults={cartLine.properties}
                />
                <StyledWrapper {...styles.cartLineQuantityWrapper}>
                    <CartLineQuantity
                        cart={cart as Cart}
                        editable={true}
                        extendedStyles={styles.cartLineQuantityStyles}
                        onQtyOrderedChange={(inputQty: number) => {
                            qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                        }}
                    />
                    {showWarning && (
                        <Tooltip
                            triggerComponent={<IconMemo src={NotificationWarning} color="warning" size={20} />}
                            text={[
                                ...(errorMessages ?? []),
                                cartLine.quoteRequired ? siteMessage("Rfq_TooltipMessage") : "",
                            ]
                                .filter(Boolean)
                                .join(", ")}
                        />
                    )}
                </StyledWrapper>
                {!cart?.cartNotPriced && (
                    <ProductPrice
                        showLabel={false}
                        showSavings={false}
                        product={cartLine}
                        extendedStyles={styles.basicProductPriceStyles}
                        showSalePriceLabel={false}
                    />
                )}
                <Link
                    onClick={() => {
                        setShowRemoveConfirmation(true);
                    }}
                    data-test-selector="cartline_removeLine"
                    aria-label="Cartline Remove Line"
                    {...styles.removeCartLineLink}
                />
                {showRemoveConfirmation && (
                    <StyledWrapper {...styles.removeConfirmation} aria-expanded={showRemoveConfirmation}>
                        <Typography {...styles.removeConfirmationText}>{translate("Are You Sure?")}</Typography>
                        <Button
                            onClick={() => {
                                removeCartLineClickHandler(cartLine);
                            }}
                            {...styles.removeConfirmationButton}
                            aria-label={translate("Yes, Remove")}
                        >
                            <ButtonIcon src={Check} size={12} />
                            {translate("Yes, Remove")}
                        </Button>
                        <Button
                            onClick={() => {
                                setShowRemoveConfirmation(false);
                            }}
                            {...styles.removeConfirmationButton}
                            aria-label={translate("No")}
                        >
                            <ButtonIcon src={Close} size={12} />
                            {translate("No")}
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
